import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminApi, DashboardStats } from '../services/adminApi';
import './AdminDashboard.css';
import { proxyBucketUrl, handleImageError } from '../utils/imageUtils';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="dashboard-loading" style={{
          background: '#000000',
          color: '#ffffff'
        }}>
          <div className="loading-spinner"></div>
          <p style={{color: '#e5e7eb'}}>Loading Dashboard...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="dashboard-error" style={{
          background: '#000000',
          color: '#ffffff'
        }}>
          <div className="error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V13M12 17.0195V17M10.29 3.86L1.82 18C1.64468 18.3024 1.55274 18.6453 1.55376 18.9945C1.55478 19.3437 1.64868 19.6861 1.82601 19.9877C2.00333 20.2893 2.25952 20.5368 2.56983 20.7039C2.88014 20.871 3.22929 20.9519 3.58 20.94H20.42C20.7707 20.9519 21.1199 20.871 21.4302 20.7039C21.7405 20.5368 21.9967 20.2893 22.174 19.9877C22.3513 19.6861 22.4452 19.3437 22.4462 18.9945C22.4473 18.6453 22.3553 18.3024 22.18 18L13.71 3.86C13.5318 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3438 2.89725 12 2.89725C11.6562 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4682 3.56611 10.29 3.86V3.86Z" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h3 style={{color: '#ffffff'}}>Error Loading Dashboard</h3>
          <p style={{color: '#e5e7eb'}}>{error}</p>
          <button onClick={loadDashboardStats} className="retry-btn">
            Try Again
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-dashboard" style={{background: '#000000', color: '#ffffff'}}>
        {/* Dashboard Header */}
        <div className="dashboard-header" style={{
          background: 'linear-gradient(145deg, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.95))',
          border: '1px solid rgba(144, 14, 155, 0.3)',
          borderRadius: '20px',
          color: '#ffffff'
        }}>
          <div className="header-content">
            <h1 style={{color: '#ffffff'}}>Dashboard Overview</h1>
            <p style={{color: '#e5e7eb'}}>Welcome back! Here's what's happening with your store today.</p>
          </div>
          <div className="header-actions">
            <button onClick={loadDashboardStats} className="refresh-btn">
              <span className="btn-icon">🔄</span>
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card primary" style={{
            background: 'linear-gradient(145deg, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.95))',
            border: '1px solid rgba(144, 14, 155, 0.3)',
            borderRadius: '20px',
            color: '#ffffff'
          }}>
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="12" y1="1" x2="12" y2="23" stroke="#27ae60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="#27ae60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{color: '#ffffff'}}>€{Number(stats?.overview.totalRevenue || 0).toFixed(2)}</div>
              <div className="stat-label" style={{color: '#e5e7eb'}}>Total Revenue</div>
            </div>
          </div>

          <div className="stat-card success" style={{
            background: 'linear-gradient(145deg, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.95))',
            border: '1px solid rgba(144, 14, 155, 0.3)',
            borderRadius: '20px',
            color: '#ffffff'
          }}>
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 8C21 8 21 8 21 8L12 13L3 8" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 8V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V8" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{color: '#ffffff'}}>{stats?.overview.totalOrders || 0}</div>
              <div className="stat-label" style={{color: '#e5e7eb'}}>Total Orders</div>
            </div>
          </div>

          <div className="stat-card warning" style={{
            background: 'linear-gradient(145deg, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.95))',
            border: '1px solid rgba(144, 14, 155, 0.3)',
            borderRadius: '20px',
            color: '#ffffff'
          }}>
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#f39c12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="9" cy="7" r="4" stroke="#f39c12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#f39c12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#f39c12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{color: '#ffffff'}}>{stats?.overview.totalUsers || 0}</div>
              <div className="stat-label" style={{color: '#e5e7eb'}}>Total Customers</div>
            </div>
          </div>

          <div className="stat-card info" style={{
            background: 'linear-gradient(145deg, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.95))',
            border: '1px solid rgba(144, 14, 155, 0.3)',
            borderRadius: '20px',
            color: '#ffffff'
          }}>
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 4V2C7 1.44772 7.44772 1 8 1H16C16.5523 1 17 1.44772 17 2V4H20C20.5523 4 21 4.44772 21 5C21 5.55228 20.5523 6 20 6H19V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V6H4C3.44772 6 3 5.55228 3 5C3 4.44772 3.44772 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z" fill="#900e9b"/>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value" style={{color: '#ffffff'}}>{stats?.overview.totalProducts || 0}</div>
              <div className="stat-label" style={{color: '#e5e7eb'}}>Total Products</div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="dashboard-content">
          {/* Recent Orders */}
          <div className="dashboard-card" style={{
            background: 'linear-gradient(145deg, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.95))',
            border: '1px solid rgba(144, 14, 155, 0.3)',
            borderRadius: '20px',
            color: '#ffffff'
          }}>
            <div className="card-header" style={{
              background: 'rgba(144, 14, 155, 0.1)',
              borderBottom: '1px solid rgba(144, 14, 155, 0.3)'
            }}>
              <h3 style={{color: '#ffffff'}}>Recent Orders</h3>
              <span className="pending-badge">
                {stats?.overview.pendingOrders || 0} pending
              </span>
            </div>
            <div className="card-content">
              {stats?.recentOrders && stats.recentOrders.length > 0 ? (
                <div className="orders-list">
                  {stats.recentOrders.map((order) => (
                    <div key={order.id} className="order-item">
                      <div className="orders-info">
                        <div className="order-number">#{order.orderNumber}</div>
                        <div className="order-customer">
                          {order.user?.firstName} {order.user?.lastName}
                        </div>
                      </div>
                      <div className="order-status">
                        <span className={`status-badge ${order.status.toLowerCase()}`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="order-amount">
                        €{Number(order.totalAmount || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{
                  background: 'transparent',
                  color: '#ffffff'
                }}>
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 8C21 8 21 8 21 8L12 13L3 8" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 8V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V8" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p style={{color: '#e5e7eb'}}>No recent orders</p>
                </div>
              )}
            </div>
          </div>

          {/* Top Products */}
          <div className="dashboard-card" style={{
            background: 'linear-gradient(145deg, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.95))',
            border: '1px solid rgba(144, 14, 155, 0.3)',
            borderRadius: '20px',
            color: '#ffffff'
          }}>
            <div className="card-header" style={{
              background: 'rgba(144, 14, 155, 0.1)',
              borderBottom: '1px solid rgba(144, 14, 155, 0.3)'
            }}>
              <h3 style={{color: '#ffffff'}}>Top Selling Products</h3>
              <span className="view-all-link" style={{color: '#667eea'}}>View All</span>
            </div>
            <div className="card-content">
              {stats?.topProducts && stats.topProducts.length > 0 ? (
                <div className="products-list">
                  {stats.topProducts.map((item, index) => (
                    <div key={item.productId} className="product-item">
                      <div className="product-rank">#{index + 1}</div>
                      <div className="product-image">
                        {item.product?.images?.[0] ? (
                          <img 
                            src={proxyBucketUrl(item.product.images[0])} 
                            alt={item.product.name} 
                            onError={handleImageError}
                          />
                        ) : (
                          <div className="image-placeholder">📷</div>
                        )}
                      </div>
                      <div className="product-info">
                        <div className="admin-product-name">{item.product?.name || 'Unknown Product'}</div>
                        <div className="product-sales">{item._sum.quantity} sold</div>
                      </div>
                      <div className="product-price">
                        €{Number(item.product?.price || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{
                  background: 'transparent',
                  color: '#ffffff'
                }}>
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 4V2C7 1.44772 7.44772 1 8 1H16C16.5523 1 17 1.44772 17 2V4H20C20.5523 4 21 4.44772 21 5C21 5.55228 20.5523 6 20 6H19V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V6H4C3.44772 6 3 5.55228 3 5C3 4.44772 3.44772 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z" fill="#900e9b"/>
                    </svg>
                  </div>
                  <p style={{color: '#e5e7eb'}}>No sales data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions" style={{
          background: 'linear-gradient(145deg, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.95))',
          border: '1px solid rgba(144, 14, 155, 0.3)',
          borderRadius: '20px',
          color: '#ffffff'
        }}>
          <div className="actions-header">
            <h3 style={{color: '#ffffff'}}>Quick Actions</h3>
          </div>
          <div className="actions-grid">
            <div className="action-card" onClick={() => navigate('/admin/products/add')} style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(144, 14, 155, 0.3)',
              borderRadius: '12px'
            }}>
              <div className="action-icon">➕</div>
              <div className="action-content">
                <h4 style={{color: '#ffffff'}}>Add Product</h4>
                <p style={{color: '#e5e7eb'}}>Create a new product listing</p>
              </div>
            </div>
            
            <a href="/admin/orders" className="action-card" style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(144, 14, 155, 0.3)',
              borderRadius: '12px',
              textDecoration: 'none'
            }}>
              <div className="action-icon">📋</div>
              <div className="action-content">
                <h4 style={{color: '#ffffff'}}>Manage Orders</h4>
                <p style={{color: '#e5e7eb'}}>View and update order status</p>
              </div>
            </a>
            
            <a href="/admin/users" className="action-card" style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(144, 14, 155, 0.3)',
              borderRadius: '12px',
              textDecoration: 'none'
            }}>
              <div className="action-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="action-content">
                <h4 style={{color: '#ffffff'}}>View Customers</h4>
                <p style={{color: '#e5e7eb'}}>Manage customer accounts</p>
              </div>
            </a>
            
            <a href="/admin/content" className="action-card" style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(144, 14, 155, 0.3)',
              borderRadius: '12px',
              textDecoration: 'none'
            }}>
              <div className="action-icon">✨</div>
              <div className="action-content">
                <h4 style={{color: '#ffffff'}}>Update Content</h4>
                <p style={{color: '#e5e7eb'}}>Manage homepage content</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;